import { useState } from 'react';
import { ChatTextIcon, PhoneIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import type { QuickReplyOption } from './tool-signals';

/**
 * FRONT-907: renders a `quick-replies` signal — tappable follow-ups under a
 * scripted assistant message, so a seeded question is a button rather than
 * dead prose (live-test feedback). A tap sends the option's text as a real
 * user message via the chat hook; the row collapses locally after use (it
 * reappears on a full reload, which is harmless — resending is idempotent
 * conversation, not an action).
 *
 * 2026-07-21: an option with `action: "call"` starts a live voice call
 * instead (the day-one interview offer's "Let's chat" / "Let's talk" pair) —
 * `onStartCall` is only required when such an option is actually present.
 *
 * 2026-07-21 review (Marko): stacked buttons read as one on top of the
 * other with no visible distinction, and a filled/outline pair implied a
 * recommendation ("chat" over "talk") that was never actually intended —
 * neither mode is preferred over the other. Side-by-side with a mode icon
 * each (chat bubble / phone) makes the choice legible at a glance instead of
 * relying on color to differentiate; both render at the same visual weight
 * regardless of `primary` for exactly this reason (a caller can still pass
 * `primary` for a genuine future recommend-one-option case).
 *
 * 2026-07-22 fix (FRONT-907): visibility is now derived from `isLastMessage`
 * rather than local state alone — a full reload used to re-show buttons the
 * user already answered, since `used` reset to its initial `false` on every
 * mount. A later turn (this card's host message is no longer last) means it
 * was answered or overtaken regardless of `used`; the local flag still
 * collapses the row instantly on tap, before the follow-up turn lands and
 * makes it not-last for real.
 */
export function QuickRepliesCard({
  options,
  onSendMessage,
  onStartCall,
  isLastMessage,
}: {
  // The signal payload's `key` field stays out of the props on purpose —
  // React reserves the name; the registry uses it as the element key.
  options: QuickReplyOption[];
  onSendMessage?: (text: string) => void | Promise<void>;
  onStartCall?: () => void;
  isLastMessage?: boolean;
}) {
  const [used, setUsed] = useState(false);

  if (!options?.length || used || !isLastMessage) return null;

  // Side-by-side reads as a genuine choice between two equal modes; three or
  // more options (a plain follow-up prompt list) keeps the original stacked
  // layout, where a row would wrap awkwardly.
  const asRow = options.length === 2;

  const layoutClass = asRow ? 'flex-row' : 'flex-col';

  return (
    <div className={`flex ${layoutClass} items-stretch gap-2 pt-1 max-w-[85%]`}>
      {options.map((option) => {
        const isCall = option.action === 'call';
        // Aikido (keep lines readable): a nested ternary here was hard to
        // parse. In the row layout every button is 'outline' regardless of
        // `primary` (see the file-level note on why neither mode is
        // recommended) — only the stacked layout ever honors `primary`.
        const variant = !asRow && option.primary ? 'default' : 'outline';
        return (
          <Button
            key={option.label}
            type="button"
            size="lg"
            variant={variant}
            className={asRow ? 'flex-1 gap-2 text-base' : undefined}
            disabled={isCall ? !onStartCall : !onSendMessage}
            onClick={() => {
              setUsed(true);
              if (isCall) {
                onStartCall?.();
              } else {
                void onSendMessage?.(option.send);
              }
            }}
          >
            {asRow &&
              (isCall ? (
                <PhoneIcon size={16} weight="regular" />
              ) : (
                <ChatTextIcon size={16} weight="regular" />
              ))}
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
